import { DoorOpen, Warehouse, Cog, KeyRound, DoorClosed, Zap, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { TEL_HREF } from "@/siteConfig";

const SERVICES = [
  {
    id: "cancelli",
    icon: DoorOpen,
    title: "Cancelli",
    tagline: "Automatici bloccati e problemi di apertura",
    items: [
      "Cancelli automatici bloccati",
      "Problemi di apertura",
      "Motori",
      "Fotocellule",
      "Automazioni",
    ],
  },
  {
    id: "serrande",
    icon: Warehouse,
    title: "Serrande",
    tagline: "Elettriche o manuali, anche bloccate",
    items: [
      "Serrande elettriche o manuali bloccate",
      "Problemi di apertura e chiusura",
    ],
  },
  {
    id: "automazioni",
    icon: Cog,
    title: "Automazioni",
    tagline: "Elettronica e meccanica degli impianti",
    items: [
      "Motori",
      "Centraline",
      "Fotocellule",
      "Telecomandi",
      "Malfunzionamenti",
    ],
  },
  {
    id: "serrature",
    icon: KeyRound,
    title: "Serrature e Accessi",
    tagline: "Accessi bloccati e interventi urgenti",
    items: [
      "Problemi di serrature e accessi",
      "Interventi urgenti quando appropriato",
    ],
  },
  {
    id: "apertura-porte",
    icon: DoorClosed,
    title: "Apertura Porte",
    tagline: "Servizio fabbro urgente — chiuso fuori casa",
    items: [
      "Porte di casa chiuse a chiave o bloccate",
      "Porte blindate che non si aprono",
      "Chiavi dimenticate o spezzate",
      "Aperture quando tecnicamente appropriato",
    ],
  },
  {
    id: "elettrico",
    icon: Zap,
    title: "Pronto Intervento Elettrico",
    tagline: "Guasti elettrici urgenti",
    items: ["Assistenza per problemi e guasti elettrici urgenti"],
  },
];

const PAGE_LINKS = {
  cancelli: { href: "/pronto-intervento-cancelli-roma", label: "Pronto intervento cancelli Roma" },
  serrande: { href: "/pronto-intervento-serrande-roma", label: "Pronto intervento serrande Roma" },
  automazioni: { href: "/automazioni-cancelli-roma", label: "Automazioni cancelli Roma" },
  serrature: { href: "/pronto-intervento-serrature-roma", label: "Pronto intervento serrature Roma" },
  "apertura-porte": { href: "/apertura-porte-roma", label: "Apertura porte Roma" },
  elettrico: { href: "/elettricista-pronto-intervento-roma", label: "Elettricista pronto intervento Roma" },
};

export const Services = () => (
  <section
    id="servizi"
    data-testid="services-section"
    className="scroll-mt-24 py-20 sm:py-28"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <SectionHeading
        overline="Cosa ripariamo"
        title="Cancelli, serrande, automazioni, serrature, apertura porte ed elettrico"
        lead="Sei aree di intervento, un solo numero da chiamare. Descrivici il problema e verifichiamo subito la disponibilità nella tua zona."
      />

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
        {SERVICES.map((service, idx) => (
          <Reveal key={service.id} delay={idx * 0.08} className="h-full">
            <article
              data-testid={`service-card-${service.id}`}
              className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/[0.08] bg-[#111620] p-7 sm:p-8 transition-all duration-300 hover:border-amber-400/40 hover:bg-[#141a26] hover:shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
            >
              <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-400/0 blur-[70px] transition-all duration-500 group-hover:bg-amber-400/10" />
              <div className="flex items-start justify-between gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-amber-400/25 bg-amber-400/10 text-amber-400 transition-transform duration-300 group-hover:scale-110">
                  <service.icon className="h-6 w-6" />
                </div>
                <ArrowUpRight className="h-5 w-5 text-zinc-600 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-amber-400" />
              </div>
              <h3 className="mt-5 font-display text-xl sm:text-2xl font-bold text-zinc-100">
                {service.title}
              </h3>
              <p className="mt-1.5 text-sm text-zinc-500">{service.tagline}</p>
              <ul className="mt-5 flex-1 space-y-2.5">
                {service.items.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2.5 text-sm text-zinc-300"
                  >
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400/80" />
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href={TEL_HREF}
                data-testid={`service-card-${service.id}-call-link`}
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-amber-400 transition-colors hover:text-amber-300"
              >
                Chiama per questo problema
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href={PAGE_LINKS[service.id].href}
                data-testid={`service-card-${service.id}-page-link`}
                className="mt-2.5 inline-flex items-center gap-1.5 text-xs text-zinc-500 transition-colors hover:text-amber-300"
              >
                Pagina dedicata: {PAGE_LINKS[service.id].label}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
